/**
 * useHScrollSync — extraído de ForecastPage.tsx (P7 split).
 *
 * Mantiene un scrollbar horizontal inferior visible y sincronizado
 * con el host horizontal interno (macOS oculta scrollbars overlay).
 * Observa changes con ResizeObserver + MutationObserver para reflejar
 * la tabla aunque mute después de mount.
 */

import { useEffect, RefObject } from "react";

interface UseHScrollSyncOptions {
  scrollHostRef: RefObject<HTMLElement | null>;
  xScrollRef: RefObject<HTMLElement | null>;
  bottomScrollRef: RefObject<HTMLElement | null>;
  hContentRef: RefObject<HTMLElement | null>;
  setHScrollWidth: (n: number) => void;
  setBottomBarLeft: (n: number) => void;
  setBottomBarWidth: (n: number) => void;
}

export function useHScrollSync(opts: UseHScrollSyncOptions): void {
  const {
    scrollHostRef,
    xScrollRef,
    bottomScrollRef,
    hContentRef,
    setHScrollWidth,
    setBottomBarLeft,
    setBottomBarWidth,
  } = opts;

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | null = null;
    let observedContent = false;
    let observedTable = false;

    const tryInit = () => {
      if (cancelled) return;
      const container = scrollHostRef.current;
      const xScroll = xScrollRef.current;
      const bottom = bottomScrollRef.current;
      const content = hContentRef.current;

      if (!container || !xScroll || !bottom) {
        requestAnimationFrame(tryInit);
        return;
      }

      let syncing = false;

      const syncFromXScroll = () => {
        if (syncing) return;
        syncing = true;
        bottom.scrollLeft = xScroll.scrollLeft;
        syncing = false;
      };
      const syncFromBottom = () => {
        if (syncing) return;
        syncing = true;
        xScroll.scrollLeft = bottom.scrollLeft;
        syncing = false;
      };

      const measure = () => {
        const tableEl = container.querySelector("table") as HTMLElement | null;
        const contentEl = hContentRef.current;
        const contentWidth = contentEl?.scrollWidth || 0;
        const hostWidth = xScroll.scrollWidth || 0;
        const tableWidth = tableEl?.scrollWidth || 0;
        const width = Math.max(contentWidth, hostWidth, tableWidth);
        setHScrollWidth(width);
        bottom.scrollLeft = xScroll.scrollLeft;

        const rect = xScroll.getBoundingClientRect();
        setBottomBarLeft(rect.left);
        setBottomBarWidth(rect.width);
      };

      const ro = new ResizeObserver(measure);
      ro.observe(xScroll);
      if (content) {
        ro.observe(content);
        observedContent = true;
      }
      const tableElForObs = container.querySelector("table");
      if (tableElForObs instanceof HTMLElement) {
        ro.observe(tableElForObs);
        observedTable = true;
      }

      const mo = new MutationObserver(() => measure());
      if (tableElForObs)
        mo.observe(tableElForObs, { childList: true, subtree: true });

      const moLate = new MutationObserver(() => {
        const c = hContentRef.current;
        if (c && !observedContent) {
          ro.observe(c);
          observedContent = true;
        }
        const t = container.querySelector("table") as HTMLElement | null;
        if (t && !observedTable) {
          ro.observe(t);
          mo.observe(t, { childList: true, subtree: true });
          observedTable = true;
        }
        measure();
      });
      moLate.observe(xScroll, { childList: true, subtree: true });

      measure();

      xScroll.addEventListener("scroll", syncFromXScroll, { passive: true });
      bottom.addEventListener("scroll", syncFromBottom, { passive: true });

      const onResize = () => measure();
      window.addEventListener("resize", onResize, { passive: true });

      cleanup = () => {
        xScroll.removeEventListener("scroll", syncFromXScroll);
        bottom.removeEventListener("scroll", syncFromBottom);
        ro.disconnect();
        mo.disconnect();
        moLate.disconnect();
        window.removeEventListener("resize", onResize);
      };
    };

    tryInit();
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);
}

interface UseHScrollMeasureOnChangeOptions {
  scrollHostRef: RefObject<HTMLElement | null>;
  xScrollRef: RefObject<HTMLElement | null>;
  bottomScrollRef: RefObject<HTMLElement | null>;
  hContentRef: RefObject<HTMLElement | null>;
  setHScrollWidth: (n: number) => void;
  setBottomBarLeft: (n: number) => void;
  setBottomBarWidth: (n: number) => void;
  deps: unknown[];
}

export function useHScrollMeasureOnChange(
  opts: UseHScrollMeasureOnChangeOptions,
): void {
  const {
    scrollHostRef,
    xScrollRef,
    bottomScrollRef,
    hContentRef,
    setHScrollWidth,
    setBottomBarLeft,
    setBottomBarWidth,
    deps,
  } = opts;

  useEffect(() => {
    const container = scrollHostRef.current;
    const xScroll = xScrollRef.current;
    const bottom = bottomScrollRef.current;
    const content = hContentRef.current;
    if (!container || !xScroll || !bottom) return;
    const doMeasure = () => {
      const tableEl = container.querySelector("table") as HTMLElement | null;
      const contentWidth = content?.scrollWidth || 0;
      const hostWidth = xScroll.scrollWidth || 0;
      const tableWidth = tableEl?.scrollWidth || 0;
      const width = Math.max(contentWidth, hostWidth, tableWidth);
      setHScrollWidth(width);
      const rect = xScroll.getBoundingClientRect();
      setBottomBarLeft(rect.left);
      setBottomBarWidth(rect.width);
      bottom.scrollLeft = xScroll.scrollLeft;
    };
    const raf = requestAnimationFrame(() => doMeasure());
    return () => cancelAnimationFrame(raf);
  }, deps);
}
